import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../../../components/ui/icon'
import { mdiCheckCircleOutline, mdiOpenInNew, mdiRefresh } from '@mdi/js'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useJobsBlockers, type JobsBlocker } from '../../../api/jobs'
import { useLiveSubscription, useScopeLive } from '../../../contexts/LiveEventsContext'
import { useIsPlatformAdmin } from '../../../hooks/useAuth'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { EmptyState } from '../../../components/ui/empty-state'

export const Route = createFileRoute('/$org/jobs/blockers')({
    component: JobsBlockersPage,
})

/**
 * JobsBlockersPage lists the org's processing blockers — the human-actionable
 * items that stop a queue from draining on its own (spec 2026-08-18-01 §4).
 *
 * Deliberately CTA-only: accepting a document-type proposal requires reviewing
 * its full payload, which is DocumentTypeProposalCard's job on the document
 * detail page. This page routes you there, it does not inline the decision.
 */
function JobsBlockersPage() {
    const { org } = useParams({ from: '/$org/jobs/blockers' })
    const { t } = useTranslation('jobs')

    // Same live-scope pattern as the jobs list: resolving a proposal
    // re-enqueues the paused backlog, which dirties the `jobs` scope and
    // makes the resolved row disappear without a manual reload.
    useLiveSubscription({ entity: 'jobs' })
    const isJobsScopeLive = useScopeLive({ entity: 'jobs' })
    const { data, isLoading, isFetching, error, refetch } = useJobsBlockers(org, isJobsScopeLive)

    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('list.title'), path: `/${org}/jobs` },
        { title: t('blockers.title') },
    ])

    const blockers = data?.blockers ?? []

    return (
        <div data-testid="jobs-blockers-page">
            <PageHeader
                className="mb-6"
                icon={SECTION_ICONS.jobs}
                title={t('blockers.title')}
                titleAside={<span aria-hidden="true">⛔</span>}
                actions={
                    <Button variant="outline" onClick={() => refetch()} data-testid="jobs-blockers-refresh">
                        <AppIcon path={mdiRefresh} size="md" className="mr-2" spin={isFetching} />
                        {t('common:actions.refresh')}
                    </Button>
                }
            />

            <p className="text-muted-foreground mb-6">{t('blockers.description')}</p>

            {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <p className="text-destructive">
                        {(error as any)?.title || (error as any)?.message || t('common:status.error')}
                    </p>
                </div>
            )}

            {!error && isLoading && (
                <p className="text-muted-foreground">{t('blockers.loading')}</p>
            )}

            {!error && !isLoading && blockers.length === 0 && (
                <Card data-testid="jobs-blockers-empty">
                    <CardContent className="p-0">
                        <EmptyState
                            className="bg-transparent"
                            icon={mdiCheckCircleOutline}
                            title={t('blockers.empty_title')}
                            description={t('blockers.empty_description')}
                        />
                    </CardContent>
                </Card>
            )}

            {!error && blockers.length > 0 && (
                <div className="space-y-4">
                    {blockers.map((blocker, index) => (
                        <BlockerRow key={`${blocker.kind}-${blocker.entity_uid ?? index}`} org={org} blocker={blocker} />
                    ))}
                </div>
            )}
        </div>
    )
}

function BlockerRow({ org, blocker }: { org: string; blocker: JobsBlocker }) {
    const { t } = useTranslation('jobs')
    const isPlatformAdmin = useIsPlatformAdmin()

    // Unknown codes must never render a blank row: a blocker kind added by a
    // newer backend still has to say *something* actionable.
    const title = t(`blockers.kind.${blocker.kind}.title`, { defaultValue: blocker.kind })
    // The provider's own code and the outage's start feed the copy, so
    // `invalid_api_key` can name what Google actually said instead of a
    // generic "something is wrong with the key".
    const description = t(`blockers.kind.${blocker.kind}.description`, {
        defaultValue: '',
        reason: blocker.reason || '—',
        date: blocker.since ? new Date(blocker.since).toLocaleString() : '—',
    })

    return (
        <Card data-testid={`blocker-row-${blocker.kind}`}>
            <CardContent className="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex gap-3">
                    <span aria-hidden="true" className="text-xl leading-none">⛔</span>
                    <div className="space-y-1">
                        <p className="font-medium">{title}</p>
                        {description && <p className="text-sm text-muted-foreground">{description}</p>}
                        <p className="text-sm text-muted-foreground" data-testid={`blocker-blocked-count-${blocker.kind}`}>
                            {t('blockers.blocked_count', { count: blocker.blocked_count })}
                            {blocker.since && (
                                <> · {t('blockers.since', { date: new Date(blocker.since).toLocaleString() })}</>
                            )}
                        </p>
                        {blocker.entity_type === 'document' && blocker.entity_uid && (
                            <Link
                                to="/$org/documents/$docUid"
                                params={{ org, docUid: blocker.entity_uid }}
                                className="text-sm underline"
                                data-testid={`blocker-entity-link-${blocker.kind}`}
                            >
                                {blocker.entity_name || blocker.entity_uid}
                            </Link>
                        )}
                    </div>
                </div>

                <BlockerAction org={org} blocker={blocker} isPlatformAdmin={isPlatformAdmin} />
            </CardContent>
        </Card>
    )
}

function BlockerAction({
    org,
    blocker,
    isPlatformAdmin,
}: {
    org: string
    blocker: JobsBlocker
    isPlatformAdmin: boolean
}) {
    const { t } = useTranslation('jobs')

    if (blocker.action === 'review_proposal' && blocker.entity_uid) {
        return (
            <Button asChild data-testid={`blocker-cta-${blocker.action}`}>
                <Link to="/$org/documents/$docUid" params={{ org, docUid: blocker.entity_uid }}>
                    <AppIcon path={mdiOpenInNew} size="md" className="mr-2" />
                    {t('blockers.action.review_proposal')}
                </Link>
            </Button>
        )
    }

    if (blocker.action === 'configure_gemini_key') {
        // Only a platform admin can reach /admin/settings; everyone else gets
        // the explanatory hint rather than a button that would 403.
        if (!isPlatformAdmin) {
            return (
                <p className="text-sm text-muted-foreground" data-testid="blocker-cta-configure_gemini_key-hint">
                    {t('blockers.action.configure_gemini_key_hint')}
                </p>
            )
        }
        return (
            <Button asChild data-testid={`blocker-cta-${blocker.action}`}>
                <Link to="/admin/settings">
                    <AppIcon path={mdiOpenInNew} size="md" className="mr-2" />
                    {t('blockers.action.configure_gemini_key')}
                </Link>
            </Button>
        )
    }

    return null
}
